import type { TreeDataNode } from 'antd';
import { IFarm } from 'interfaces/IFarm';
import { IPaddock } from 'interfaces/IPaddock';
import {
  inputMatchesTreeSearch,
  paddockMatchesTreeSearch,
} from 'utils/FarmUtils';

import { CopyTreeIndex } from './buildCopyTreeData';

/** Every paddock by id, for matching paddock names. */
export const indexPaddocksById = (farms: IFarm[]): Map<string, IPaddock> =>
  new Map(
    farms.flatMap((farm) =>
      farm.paddocks.map((paddock) => [paddock.paddockId, paddock] as const)
    )
  );

/**
 * The tree as searched, by the sidebar's rules: a paddock whose name matches
 * keeps all its documents, otherwise only the documents whose crop or variety
 * matches; farms with nothing left are dropped. Only what's shown is
 * filtered, so ticks hidden by a search are kept. `term` must be normalised,
 * and `index` must still hold the Source document, so it can match too.
 */
export const searchCopyTree = (
  treeData: TreeDataNode[],
  index: CopyTreeIndex,
  paddocksById: Map<string, IPaddock>,
  term: string
): TreeDataNode[] => {
  if (!term) return treeData;
  return treeData.flatMap((farm) => {
    const paddocks = (farm.children ?? []).flatMap((paddockNode) => {
      const info = index.get(String(paddockNode.key));
      if (info?.kind !== 'paddock') return [];
      const paddock = paddocksById.get(info.paddockId);
      if (paddock && paddockMatchesTreeSearch(paddock, term)) {
        return [paddockNode];
      }
      const children = (paddockNode.children ?? []).filter((node) => {
        const input = index.get(String(node.key));
        return (
          input?.kind === 'input' && inputMatchesTreeSearch(input.input, term)
        );
      });
      return children.length > 0 ? [{ ...paddockNode, children }] : [];
    });
    return paddocks.length > 0 ? [{ ...farm, children: paddocks }] : [];
  });
};

/** Every farm and every paddock with documents: the default expansion, and while searching, so all matches show. */
export const getPaddockExpandedKeys = (treeData: TreeDataNode[]): string[] =>
  treeData.flatMap((farm) => [
    String(farm.key),
    ...(farm.children ?? [])
      .filter((paddock) => (paddock.children ?? []).length > 0)
      .map((paddock) => String(paddock.key)),
  ]);
